// eslint-disable-next-line spaced-comment
///  <reference types="vite/client" />

import { Button } from '@tunasong/ui-lib'
import { graphHooks } from '@tunasong/graph-lib/react'
import { Edit as EditIcon, Save as SaveIcon, Close as CloseIcon } from '@tunasong/icons'
import { dayjs } from '@tunasong/models'
import type { CalendarEvent } from '@tunasong/schemas'
import { isPersisted } from '@tunasong/schemas'
import { AvatarList, HBox, Typography, VBox } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useState } from 'react'
import { CalendarEventEdit } from './event-edit.js'
import styles from './event-view.module.css'
import { z } from 'zod'

import type { HTMLAttributes } from 'react'

export interface CalendarEventViewProps extends HTMLAttributes<HTMLDivElement> {
  event: CalendarEvent
}

const env = z
  .object({
    VITE_GOOGLE_MAPS_API_KEY: z.string().optional(),
  })
  .parse(import.meta.env)

export const CalendarEventView: FC<CalendarEventViewProps> = props => {
  const { event: initialEvent, ...divProps } = props

  const [isEditing, setIsEditing] = useState(false)
  const [event, setEvent] = useState(initialEvent)

  const updateEntity = graphHooks.useEntityUpdate()

  const userIds = event?.attendees?.map(a => a.userId) ?? []
  const date = dayjs(event.start).format('LLL')

  // Create Google Maps embed URL from location
  const mapsEmbedUrl = event.location
    ? `https://www.google.com/maps/embed/v1/place?key=${env.VITE_GOOGLE_MAPS_API_KEY}&q=${encodeURIComponent(event.location)}`
    : null

  const handleEdit = () => setIsEditing(true)
  const handleCancel = () => {
    setEvent(initialEvent)
    setIsEditing(false)
  }
  const handleSave = () => {
    if (isPersisted(event)) {
      updateEntity(event.id, event)
    }
    setIsEditing(false)
  }
  const handleChange = (partialEvent: Partial<CalendarEvent>) => {
    setEvent(prev => ({ ...prev, ...partialEvent }))
  }

  if (isEditing) {
    return (
      <div {...divProps}>
        <CalendarEventEdit includeTitle={false} event={event} onPartialChange={handleChange} />
        <HBox className={styles.editActions}>
          <Button startIcon={<CloseIcon />} onClick={handleCancel}>
            Cancel
          </Button>
          <Button startIcon={<SaveIcon />} variant="contained" onClick={handleSave}>
            Save
          </Button>
        </HBox>
      </div>
    )
  }

  return (
    <div {...divProps}>
      <div className={styles.infoGrid}>
        <VBox>
          <Typography variant="subtitle1">Event Date</Typography>
          <Typography variant="body2">{date}</Typography>
        </VBox>

        <HBox className={styles.avatarRow}>
          <AvatarList userIds={userIds} />
          {isPersisted(event) && (
            <Button size="small" startIcon={<EditIcon />} onClick={handleEdit}>
              Edit
            </Button>
          )}
        </HBox>
      </div>

      {event.location ? (
        <VBox className={styles.locationSection}>
          <Typography variant="subtitle1">Location</Typography>
          <Typography variant="body2" className={styles.locationText}>
            {event.location}
          </Typography>
          <div className={styles.mapContainer}>
            <iframe
              width="100%"
              height="100%"
              className={styles.mapIframe}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
              src={mapsEmbedUrl ?? undefined}
            />
          </div>
        </VBox>
      ) : null}
    </div>
  )
}
